// Shared viewBox pan/zoom wiring for the schematic/PCB viewers (EcadDocument and AllSpiceDiffView):
// drag-to-pan, double-click / modifier+scroll zoom, click-through to the inspector, and
// select-region suppression. They differ only in how they resolve the SVG and inspector target.

import {ViewBoxController} from './viewbox_controller.ts';
import {getElementBounds} from '../document_highlight.ts';
import {selectRegionParent} from '../../features/selectregion.ts';
import {isAppleDevice, waitForRender} from '../../domutils.ts';

const ZOOM_STEP = 0.3;
const MAX_SCALE = 50;
// Movement past this many pixels turns a press into a pan; below it it stays a click (reaches the inspector).
const DRAG_THRESHOLD = 3;

export type ViewBoxPanZoomHandle = {
  controller: ViewBoxController;
  /** 'Shift' on Apple, 'Ctrl' elsewhere — the modifier shown in the zoom help text. */
  keyModifier: string;
  /** Tear down the ResizeObserver and the controller.  Call from the host's unmount hook. */
  destroy: () => void;
};

export type ViewBoxPanZoomOptions = {
  /** The .pan-zoom element that receives pointer/wheel/click events. */
  elem: HTMLElement;
  /** The .selectable-region the SVG viewport fills (the controller sizes it). */
  region: HTMLElement;
  /** The SVG whose viewBox is driven. */
  svg: SVGSVGElement;
  /** Element that should receive the synthesized 'inspector-click' event on a plain click. */
  inspectorTarget: HTMLElement | SVGElement;
  /** Called on each scroll-zoom, so the host can hide its zoom help text. */
  onScrollZoom?: () => void;
  /** Called after each user zoom gesture with the zoom level reached. */
  onUserZoom?: (scale: number) => void;
};

/**
 * Wire viewBox-based pan/zoom onto `elem`: drag-to-pan, double-click and modifier+scroll zoom,
 * click-through to the inspector, and select-region suppression. Driving zoom via the SVG viewBox
 * (no CSS transform) keeps the schematic crisp at any zoom in WebKit and getScreenCTM reliable.
 * Returns the controller, help-text modifier, and a destroy() (the element's own listeners die with it).
 */
export function setupViewBoxPanZoom(opts: ViewBoxPanZoomOptions): ViewBoxPanZoomHandle {
  const {elem, region, svg, inspectorTarget, onScrollZoom, onUserZoom} = opts;

  const isApple = isAppleDevice();
  const keyModifier = isApple ? 'Shift' : 'Ctrl';

  let dblClickTimer: number | null = null;

  const controller = new ViewBoxController([svg], region, {minScale: 1, maxScale: MAX_SCALE});

  const resizeObserver = new ResizeObserver(() => {
    requestAnimationFrame(() => controller.onResize());
  });
  resizeObserver.observe(region);

  // Suspended while a selection is being drawn, so drag-to-pan doesn't fight the selection drag.
  let disabled = false;

  // Drag to pan. Only movement past the threshold counts as a pan (so a jittery click still clicks),
  // and the pointer is captured only once a real drag starts so a plain click's `click` event fires.
  let dragging = false;
  let movedDuringDrag = false;
  let downX = 0;
  let downY = 0;
  let lastX = 0;
  let lastY = 0;

  elem.addEventListener('click', (e) => {
    if (e.detail !== 1) return;
    e.preventDefault();
    const wasDrag = movedDuringDrag;
    dblClickTimer = Number(setTimeout(() => {
      // If the press turned into a pan, don't treat it as an inspector click.
      if (wasDrag) return;
      const svgClickEvent = new CustomEvent('inspector-click', {detail: e});
      inspectorTarget.dispatchEvent(svgClickEvent);
    }, 200));
  });

  elem.addEventListener('pointerdown', (e) => {
    if (disabled) return;
    dragging = true;
    movedDuringDrag = false;
    downX = lastX = e.clientX;
    downY = lastY = e.clientY;
  });
  elem.addEventListener('pointermove', (e) => {
    if (!dragging) return;
    if (!movedDuringDrag) {
      if (Math.hypot(e.clientX - downX, e.clientY - downY) < DRAG_THRESHOLD) return;
      movedDuringDrag = true;
      elem.setPointerCapture(e.pointerId);
    }
    controller.panByClient(e.clientX - lastX, e.clientY - lastY);
    lastX = e.clientX;
    lastY = e.clientY;
  });
  const endDrag = (e: PointerEvent) => {
    if (!dragging) return;
    dragging = false;
    try {
      elem.releasePointerCapture(e.pointerId);
    } catch {
      // pointer may already be released
    }
  };
  elem.addEventListener('pointerup', endDrag);
  elem.addEventListener('pointercancel', endDrag);

  // WebKit can still select SVG text on click/drag despite user-select:none — suppress it outright.
  elem.addEventListener('selectstart', (e) => e.preventDefault());

  // Seed from the current mode: if expanded while already in select mode, the change event
  // would have fired before this listener existed.
  const fileBoxElem = selectRegionParent(elem);
  if (fileBoxElem) {
    disabled = fileBoxElem.classList.contains('select-region-mode');
    elem.style.cursor = disabled ? '' : 'default';
    fileBoxElem.addEventListener('selectregionmodechange', (event: Event) => {
      disabled = Boolean((event as CustomEvent).detail.isSelectMode);
      elem.style.cursor = disabled ? '' : 'default';
    });
  }

  // Double-click zooms in; Option (or Ctrl on Windows) + double-click zooms out.
  elem.addEventListener('dblclick', (event) => {
    clearTimeout(dblClickTimer);
    dblClickTimer = null;
    if (disabled) return;
    // Bail on other modifiers so we don't interfere with their shortcuts.
    let zoomIn: boolean;
    if (isApple) {
      if (event.ctrlKey || event.metaKey || event.shiftKey) return;
      zoomIn = !event.altKey;
    } else {
      if (event.altKey || event.metaKey || event.shiftKey) return;
      zoomIn = !event.ctrlKey;
    }

    event.preventDefault();

    const requestedScale = controller.getScale() * (zoomIn ? 1.5 : 0.5);
    const reachedScale = controller.zoomToPoint(requestedScale, event, {animate: true});
    onUserZoom?.(reachedScale);
  });

  // Scroll wheel or scroll gesture to zoom.
  // eslint-disable-next-line github/require-passive-events
  elem.addEventListener('wheel', (event) => {
    // Bail on other modifiers so we don't interfere with their shortcuts.
    if (isApple) {
      if (!(!event.altKey && !event.ctrlKey && !event.metaKey &&
          event.shiftKey)) {
        return;
      }
    } else if (!(!event.altKey && event.ctrlKey && !event.metaKey &&
        !event.shiftKey)) {
      return;
    }

    event.preventDefault();

    if (disabled) return;

    const scale = controller.getScale();
    // macOS maps Shift + vertical scroll to horizontal, so fall back to deltaX.
    let delta = event.deltaY === 0 && event.deltaX ?
      event.deltaX :
      event.deltaY;
    let invertDirection = false;
    // Flip wheel direction on Apple. webkitDirectionInvertedFromDevice detects natural scrolling but
    // only exists on Safari; when undefined (other browsers) assume natural scrolling is on.
    // See https://github.com/w3c/uievents/issues/57 and https://bugs.chromium.org/p/chromium/issues/detail?id=156551
    if (isApple && (
      !('webkitDirectionInvertedFromDevice' in event) ||
      event.webkitDirectionInvertedFromDevice)
    ) {
      invertDirection = true;
    }
    if (event.deltaMode === 0) {
      // Delta in pixels: scale by the actual amount. See https://github.com/timmywil/panzoom/issues/586
      if (invertDirection) delta *= -1;
      const reachedScale = controller.zoomToPoint(scale * Math.exp((delta * ZOOM_STEP * -1) / 300), event);
      onUserZoom?.(reachedScale);
    } else {
      // Non-pixel delta: fall back to direction only.
      let wheel = delta < 0 ? 1 : -1;
      if (invertDirection) wheel *= -1;
      const reachedScale = controller.zoomToPoint(scale * Math.exp((wheel * ZOOM_STEP) / 3), event);
      onUserZoom?.(reachedScale);
    }

    // Hide the help text after the first scroll.
    onScrollZoom?.();
  });

  return {
    controller,
    keyModifier,
    destroy() {
      resizeObserver.disconnect();
      controller.destroy();
    },
  };
}

export type DollyZoomOptions = {
  /** When set, keep this zoom level and only pan to the element (zoom lock). */
  lockedScale?: number;
};

/**
 * Zoom and pan to frame the elements matched by the selector. Maps the element's screen rect back
 * through the SVG's screen CTM into zoom-invariant user (viewBox) space, so the controller frames it
 * in one shot. getScreenCTM is reliable because zoom lives in the viewBox, not an ancestor transform.
 */
export async function dollyZoomToSelector(
  // Typed structurally (not as ViewBoxController) so a Vue-reactive `this.panZoom`, whose private
  // fields are stripped by reactivity typing, stays assignable.
  controller: Pick<ViewBoxController, 'reset' | 'fitBounds' | 'centerOn'>,
  svgEl: SVGSVGElement,
  selector: string,
  options: DollyZoomOptions = {},
): Promise<void> {
  // Reset to the fit view before measuring: getElementBounds skips off-viewport elements, so an
  // off-screen same-page result would otherwise measure empty. reset() is synchronous, so only the
  // final framed view paints (no flash).
  controller.reset();
  const screen = getElementBounds(svgEl, selector);
  const ctm = svgEl.getScreenCTM();
  // No match leaves bounds inverted (minX > maxX); also bail without a CTM.
  if (!ctm || screen.maxX < screen.minX || screen.maxY < screen.minY) return;

  const inv = ctm.inverse();
  const topLeft = new DOMPoint(screen.minX, screen.minY).matrixTransform(inv);
  const bottomRight = new DOMPoint(screen.maxX, screen.maxY).matrixTransform(inv);
  const elementBounds = {
    x: topLeft.x,
    y: topLeft.y,
    w: bottomRight.x - topLeft.x,
    h: bottomRight.y - topLeft.y,
  };

  if (options.lockedScale === undefined) {
    controller.fitBounds(elementBounds);
  } else {
    controller.centerOn(elementBounds, options.lockedScale);
  }
  await waitForRender();
}
